import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Heading,
  Stack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Spinner,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  VStack,
  FormControl,
  FormLabel,
  Checkbox,
  useTheme,
  Spacer,
  useColorModeValue
} from "@chakra-ui/react";

import EditableText from "../EditableText/EditableText";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, Auth } from "../../lib/Auth";
import dialogManager from "../DialogManager/DialogManager";
import ReactSelect from "react-select";
import moment from "moment";

import "./PhoneSettings.css"; // Import the CSS file

function PhoneNumbers() {
  const theme = useTheme();
  const [phoneNumbersData, setPhoneNumbersData] = useState({
    phones: [],
    DNIs: [],
    empty_dnis: [],
    routings: [], // Initialize routings as an empty array
  });
  const [defaultRouting, setDefaultRouting] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState("");
  const [phoneNumerToPort, setPhoneNumerToPort] = useState("");
  const [selectedTags, setSelectedTags] = useState({});
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [poolSize, setPoolSize] = useState(1);
  const [currentPhone, setCurrentPhone] = useState(null); // To keep track of which phone's pool size is being edited
  const [currentPhoneAreaCode, setCurrentPhoneAreaCode] = useState(null); // To keep track of which phone's pool size is being edited
  const routingUpdateProgress = {};
  const headerBackgroundColor = useColorModeValue( '#F1F1F5', '#303343');
  const backgroundColor = useColorModeValue( '#FFFFFF', '#212330');
  const headerTextColor = useColorModeValue( '#092728', '#F9F9FB');
  const hoverBackgroundColor = useColorModeValue('#212330', '#FFFFFF');

  const {
    isOpen: isConfigOpen,
    onOpen: onConfigOpen,
    onClose: onConfigClose,
  } = useDisclosure();
  const {
    isOpen: isBuyNumOpen,
    onOpen: onBuyNumOpen,
    onClose: onBuyNumClose,
  } = useDisclosure();
  const {
    isOpen: isPortInOpen,
    onOpen: onPortInOpen,
    onClose: onPortInClose,
  } = useDisclosure();
  const [isSavingPoolSize, setIsSavingPoolSize] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isSavingPort, setIsSavingPort] = useState(false);

  const getNumberInfo = (phone, allcampaigns) => {
    const isDNI = "size" in phone;
    const _id = isDNI ? phone.sid : phone._id;
    const campaigns = allcampaigns.filter((c) =>
      c.numbers.find((n) => n.id === _id)
    );
    const name = phone.main_number
      ? "Main number"
      : campaigns.map((i) => i.name).join(",") || "No campaign";
    return {
      isDNI,
      _id,
      main_number: phone.main_number,
      campaigns,
      name,
      ...(isDNI
        ? {
          DNI: _id,
          areacode: phone.phone,
          exceptions: phone.exceptions,
          apply_exceptions_on_landing: phone.apply_exceptions_on_landing,
          phone_number: phone.phone_number,
        }
        : {
          sid: phone.sid,
        }),
      title: isDNI
        ? "DNI" + (phone.phone ? ` (${phone.phone})` : "")
        : phone.phone,
      calls: phone.calls || {},
      routing: phone.routing,
      size: phone.size,
      actions: isDNI
        ? phone.phone
          ? ["Release", "Change pool size", "Configure"]
          : ["Setup"]
        : phone.main_number
          ? phoneNumbersData.emergency &&
          phoneNumbersData.emergency.emergencyAddressSid
            ? phoneNumbersData.emergency.emergencyStatus === "Active"
              ? ["911 is Active"]
              : phoneNumbersData.emergency.emergencyStatus === "Inactive"
                ? ["911 is Inactive"]
                : [phoneNumbersData.emergency.emergencyStatus]
            : ["Emergency address registration pending ..."]
          : ["Release", "Make main"],
    };
  };

  const fetchPhoneNumbers = async () => {
    try {
      const response = await cqAPI.get("/settings/phone/numbers");
      if (!response.data) {
        return;
      }
      setPhoneNumbersData(response.data);
      setDefaultRouting(response.data.routings.find((r) => r.default));
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
    }
  };

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  useEffect(() => {
    try {
      const allPhoneNumbers = phoneNumbersData.phones
        .concat(
          phoneNumbersData.empty_dnis.map((x) => ({ ...x, size: 0, sid: x._id }))
        )
        .map((x) => getNumberInfo(x,phoneNumbersData.campaigns));
      setPhoneNumbers(allPhoneNumbers);

      // Initialize tags and routing plan options together
      const consolidatedOptions = allPhoneNumbers.reduce((acc, phone) => {
        acc[phone._id] = (phone.routing || []).map((id) => ({
          value: id,
          label: phoneNumbersData.routings.find((option) => option._id === id)?.name,
        }));
        return acc;
      }, {});
      setSelectedTags(consolidatedOptions);
    } catch (error) {
      console.error("Error managing phone numbers:", error);
    }
  }, [phoneNumbersData]);

  const postAndHandle = (ep, body, nofetch) => {
    return cqAPI.post(ep, body).then((resp) => {
      if (!resp) {
        return resp;
      }
      if (resp.type === "ok" && !nofetch) {
        return fetchPhoneNumbers();
      }
      if (resp.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: resp.msg,
          header: "Error",
        });
      }
      return resp;
    });
  };

  const handleSaveClick = async (phoneId, newValue) => {
    let updateCampaign;
    const updatedPhones = phoneNumbers.map((phone) => {
      if (phone._id === phoneId) {
        updateCampaign = { ...phone.campaigns[0], name: newValue };
        return { ...phone, name: newValue };
      }
      return phone;
    });
    setPhoneNumbers(updatedPhones);
    if (updateCampaign) {
      await postAndHandle("/settings/mt/campaign/update", {
        cid: updateCampaign._id,
        name: updateCampaign.name,
      });
      return true;
    }
    return false;
  };

  const customStyles = {
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.brand[50],
      borderRadius: '16px',
      padding: '0px 6px',
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #CBD5E0',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#2D3748',
      fontWeight: '500',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#2D3748',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#E2E8F0',
        color: '#E53E3E',
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: backgroundColor,
      borderColor: theme.colors.gray[300],
      boxShadow: theme.shadows.sm,
      "&:hover": {
        borderColor: theme.colors.gray[400],
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[50],
      zIndex: 2000, // Ensure the menu is on top of other elements
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 3000, // Higher z-index for the portal
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? theme.colors.brand[100] : theme.colors.gray[50],
      color: theme.colors.gray[700],
      "&:hover": {
        backgroundColor: theme.colors.gray[50],
      },
    }),
  };

  const handleOpenModal = (phone) => {
    setCurrentPhone(phone);
    setPoolSize(phone.size || 0); // Assuming phone.size is the current pool size
    onOpen();
  };

  const handleOpenBuyNumModal = () => {
    setCurrentPhone({});
    setPoolSize(1);
    onBuyNumOpen();
  };

  const handlePoolSizeChange = (valueAsString, valueAsNumber) => {
    // Enforce the range within the change handler
    if (valueAsNumber >= 1 && valueAsNumber <= 10) {
      setPoolSize(valueAsNumber);
    } else if (valueAsNumber < 1) {
      setPoolSize(1);
    } else if (valueAsNumber > 10) {
      setPoolSize(10);
    }
  };

  const savePoolSize = async (single) => {
    if (single && (!currentPhone._id && !currentCampaign)) {
      setAlertMessage("The source cannot be empty.");
      setIsAlertVisible(true);
      return;
    }
    if (poolSize < 1 || poolSize > 10) {
      setAlertMessage("Pool size has to be between 1 and 10.");
      setIsAlertVisible(true);
      return;
    }
    setIsSavingPoolSize(true);
    const areacode = currentPhone.areacode || currentPhoneAreaCode;
    if (!/^\d{3}/.test(areacode)) {
      setAlertMessage(
        "Please select the area code. It can only be a 3-digit number."
      );
      setIsAlertVisible(true);
      return;
    }
    const response = await cqAPI.get("/phone/lookup?areacode=" + areacode);
    setIsSavingPoolSize(false);
    if (!response) {
      onClose();
      onBuyNumClose();
      return;
    }
    if (response.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: response.msg,
        header: "Error",
      });
      return;
    }

    const required_numbers = currentPhone.size
      ? poolSize - currentPhone.size
      : poolSize;

    if (response.data.length < required_numbers) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: "There are not enough numbers available in this areacode for this DNI pool.",
        header: "Error",
      });
      return;
    }
    const numbers = response.data.splice(0, required_numbers);
    setIsSavingPoolSize(true);
    await postAndHandle("/phone/purchase", {
      phonenumber: numbers,
      campaign: currentCampaign,
      ...(single
        ? {}
        : {
          DNI: currentPhone._id,
          pool: true,
        }),
    });
    setIsSavingPoolSize(false);
    onClose();
    onBuyNumClose();
    setCurrentCampaign("")
  };

  const handleConfigOpenModal = (phone) => {
    setCurrentPhone(phone);
    onConfigOpen();
  };

  const handleSavePortIn = async () => {
    if (!/^PN[0-9a-f]{32}$/.test(phoneNumerToPort)) {
      setAlertMessage("This is not a valid SID for a Twilio phone number.");
      setIsAlertVisible(true);
      return;
    }
    setIsSavingPort(true);
    await postAndHandle("/phone/register-port", {
      sid: phoneNumerToPort,
    });
    setIsSavingPort(false);

    onPortInClose();
  };

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/; // Simplified regex for phone numbers, adjust as needed
    return phoneRegex.test(phoneNumber);
  };

  const handleSaveConfig = async () => {
    if (!validatePhoneNumber(currentPhone.phone_number)) {
      setAlertMessage("Please enter a valid phone number.");
      setIsAlertVisible(true);
      return;
    }
    setIsSavingConfig(true);

    await postAndHandle("/settings/phone/dni", {
      DNI: currentPhone._id,
      phone_number: currentPhone.phone_number,
      exceptions: currentPhone.exceptions,
      apply_exceptions_on_landing: currentPhone.apply_exceptions_on_landing,
    });

    setIsSavingConfig(false);

    onConfigClose();
  };

  const DNIConfigModal = (
    <Modal isOpen={isConfigOpen} onClose={onConfigClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Configure DNI</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {" "}
            {/* This ensures all children align to the left */}
            <FormControl id="phone-number-to-replace">
              <FormLabel textAlign="left">Phone Number to Replace</FormLabel>
              <Input
                placeholder="Enter phone number"
                value={currentPhone && currentPhone.phone_number}
                onChange={(e) =>
                  setCurrentPhone({
                    ...currentPhone,
                    phone_number: e.target.value,
                  })
                }
              />
              <Text fontSize="sm" textAlign="left">
                Format: International (e.g., +1234567890) or national.
              </Text>
            </FormControl>
            <FormControl id="keywords">
              <FormLabel textAlign="left">Keywords</FormLabel>
              <Input
                placeholder="Do not replace if the url includes any of these keywords (comma separated)"
                value={currentPhone && currentPhone.exceptions}
                onChange={(e) =>
                  setCurrentPhone({
                    ...currentPhone,
                    exceptions: e.target.value,
                  })
                }
              />
            </FormControl>
            <Checkbox
              isChecked={
                currentPhone && currentPhone.apply_exceptions_on_landing
              }
              onChange={(e) =>
                setCurrentPhone({
                  ...currentPhone,
                  apply_exceptions_on_landing: e.target.checked,
                })
              }
              textAlign="left"
            >
              {" "}
              {/* Checkbox label alignment */}
              Apply on landing page
            </Checkbox>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={handleSaveConfig}>
            Save
            {isSavingConfig && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onConfigClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const PortInModal = (
    <Modal isOpen={isPortInOpen} onClose={onPortInClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="left">Port In A Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              Make sure that you have submitted the port for this number in
              Twilio. Once submitted, get the SID from there and register it
              here:
            </Text>
            <FormControl id="phone-number-to-replace">
              <FormLabel textAlign="left">Phone Number SID</FormLabel>
              <Input
                placeholder="Enter phone number's SID"
                value={phoneNumerToPort}
                onChange={(e) => setPhoneNumerToPort(e.target.value)}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={handleSavePortIn}>
            Save
            {isSavingPort && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onPortInClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const DNIModal = (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>DNI</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              Dynamic Number Insertion (DNI) is a call tracking feature where a
              unique phone number is dynamically tied to each website traffic
              source. We forward all incoming calls to your main number while
              keeping track of all calls.
            </Text>
            {!currentPhone ||
              (!currentPhone.areacode && (
                <Text textAlign="left" mb={4}>
                  Please enter a 3-digit area code.
                </Text>
              ))}
            {!currentPhone ||
              (!currentPhone.areacode && (
                <Input
                  placeholder="Area Code"
                  onChange={(e) => setCurrentPhoneAreaCode(e.target.value)}
                  maxLength={3}
                  width="100%" // Ensure full width within the modal
                />
              ))}
            <NumberInput
              min={1}
              max={10}
              value={poolSize}
              onChange={handlePoolSizeChange}
              width="100%" // Match width with the area code input
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={() => savePoolSize(false)}>
            Save
            {isSavingPoolSize && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const BuyNumberModal = (
    <Modal isOpen={isBuyNumOpen} onClose={onBuyNumClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Phone Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text mb={4}>
              The new phone numbers can be used to enable you to track phone
              calls that are coming in from a specific source like Yelp, Google,
              etc. We forward all of incoming calls to your main number while
              keeping track of all calls.
            </Text>
            {!currentPhone ||
              (!currentPhone.areacode && (
                <Text textAlign="left" mb={4}>
                  Please enter a 3-digit area code.
                </Text>
              ))}
            <Input
              placeholder="Area Code"
              onChange={(e) => setCurrentPhoneAreaCode(e.target.value)}
              maxLength={3}
              width="100%"
            />
            <Input
              placeholder="Source"
              onChange={(e) => setCurrentCampaign(e.target.value)}
              maxLength={40}
              value={currentCampaign}
              width="100%"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={() => savePoolSize(true)}>
            Save
            {isSavingPoolSize && <Spinner size="sm" ml="2" />}
          </Button>
          <Button variant="ghost" onClick={onBuyNumClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const performAction = async (action, phone) => {
    switch (action.toLowerCase()) {
      case "release":
        await postAndHandle("/settings/phone/delete", phone);
        break;
      case "make main":
        await postAndHandle("/settings/phone/makemain", {
          _id: phone._id,
        });
        break;
      case "setup":
        handleOpenModal(phone);
        break;
      case "change pool size":
        handleOpenModal(phone);
        break;
      case "configure":
        handleConfigOpenModal(phone);
        break;
    }
  };

  const handleChange = async (newSelectedOptions, actionMeta, phone) => {
    if (routingUpdateProgress[phone._id]) {
      setAlertMessage("Wait until the previous update is completed.");
      setIsAlertVisible(true);
      return;
    }

    routingUpdateProgress[phone._id] = true;
    const selectedTagsForThisPhone = selectedTags[phone._id];

    setSelectedTags((prev) => ({
      ...prev,
      [phone._id]: newSelectedOptions,
    }));

    await postAndHandle(
      "/settings/phone/numbers/assign",
      {
        ...(phone.isDNI
          ? { DNI: phone._id }
          : { _id: phone._id }),
        routing: newSelectedOptions.map((c) => c.value),
      },
      true
    )
      .then((x) => {
        delete routingUpdateProgress[phone._id];
        if (!x) {
          setSelectedTags((prev) => ({
            ...prev,
            [phone._id]: selectedTagsForThisPhone,
          }));
        }
      })
      .catch((err) => {
        setAlertMessage(err.msg || err.message || "Generic error...");
        setIsAlertVisible(true);
        delete routingUpdateProgress[phone._id];
        setSelectedTags((prev) => ({
          ...prev,
          [phone._id]: selectedTagsForThisPhone,
        }));
      });
  };

  function formatPhoneNumber(phoneNumber) {
    // Remove all non-digit characters from the input
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');

    // Check if the number is a valid international number
    if (cleaned.length <= 10) {
      // Assume this is a US number, format as +1 (XXX) XXX-XXXX
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
      }
    } else {
      // Handle international numbers (like India)
      const internationalMatch = cleaned.match(/^(\d+)(\d{3})(\d{3})(\d{4})$/);
      if (internationalMatch) {
        return `+${internationalMatch[1]} (${internationalMatch[2]}) ${internationalMatch[3]}-${internationalMatch[4]}`;
      }
    }

    // If the number is not in a recognized format, return it as is
    return phoneNumber;
  }

  return (
    <Box className="phone-numbers-div" p={4}>
      {DNIModal}
      {DNIConfigModal}
      {BuyNumberModal}
      {PortInModal}
      {isAlertVisible && (
        <Box position="fixed" top="0" left="0" right="0" zIndex="1500">
          <Alert status="error" bg="red.500">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription display="block">{alertMessage}</AlertDescription>
            </Box>
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={() => setIsAlertVisible(false)}
            />
          </Alert>
        </Box>
      )}
      <Flex justifyContent="flex-end" mb={4} >
        <Menu>
          <MenuButton as={Button} rightIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            Actions
          </MenuButton>
          <MenuList>
            <MenuItem onClick={() => handleOpenBuyNumModal()}>Buy a Number</MenuItem>
            <MenuItem onClick={() => handleOpenModal({})}>Buy DNI</MenuItem>
            {Auth.user && Auth.user.isSuperAdmin && (
              <MenuItem onClick={onPortInOpen}>Register a Ported Number</MenuItem>
            )}
          </MenuList>
        </Menu>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="1rem" className="simple-grid" minHeight="50dvh">
        {phoneNumbers.map((phone, index) => (
          <Box // could be removed in the future
            key={index}
            className="phone-card"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p={4}
            bg={backgroundColor} // Dynamic background color
            boxShadow='base'
          >
            <Box
              className="card-header"
              p={4}
              borderTopRadius="lg"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              bg={headerBackgroundColor} // Static color for header
            >
              <Heading size="md" color={headerTextColor}>{formatPhoneNumber(phone.title)}</Heading>
              <Menu>
                <MenuButton as={IconButton} icon={<FontAwesomeIcon icon={faEllipsisV} />}
                            backgroundColor={headerBackgroundColor}
                            _hover={{bg: hoverBackgroundColor, color: backgroundColor}}
                            variant="ghost" />
                <MenuList>
                  {phone.actions.map((action, actionIndex) => (
                    <MenuItem key={actionIndex} onClick={() => performAction(action, phone)}>
                      {action}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Box>
            <Flex className="card-body" flexDirection="column" flex="1" p={4}>
              <Stack spacing={3} textAlign="left" flex="1">
                <Flex alignItems="center" className="flex-item">
                  <EditableText
                    initialValue={phone.name}
                    onSave={(e) => handleSaveClick(phone._id, e)}
                    placeholder="Name"
                    editable={!phone.main_number}
                    className="card-header"
                  />
                </Flex>
                {defaultRouting?.name && (
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>Default routing plan:</Text>
                    <Text as="b">{defaultRouting?.name}</Text>
                  </Flex>
                )}
                <Flex alignItems="center" className="flex-item">
                  <Text minWidth="120px" mr={2}>Calls:</Text>
                  <Text as="b">{phone.calls.count ? phone.calls.count : 0}</Text>
                </Flex>
                {phone.calls.avg_duration && (
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>Avg Duration:</Text>
                    <Text as="b">{Math.ceil(phone.calls.avg_duration)}</Text>
                  </Flex>
                )}
                {phone.isDNI && (
                  <Flex alignItems="center" className="flex-item">
                    <Text minWidth="120px" mr={2}>Pool Size:</Text>
                    <Text>{Math.ceil(phone.size)}</Text>
                  </Flex>
                )}
                <Flex alignItems="center" className="flex-item">
                  <Text minWidth="120px" mr={2}>Last Call:</Text>
                  <Text as="b">
                    {phone.calls.last_call ? moment(phone.calls.last_call).calendar() : "No calls"}
                  </Text>
                </Flex>
                {/* Consolidated Tags Selector */}
                <Spacer />
                <Text as="b">Incoming Routing Plan</Text>
                <ReactSelect
                  styles={customStyles}
                  isMulti
                  options={phoneNumbersData.routings.map((option) => ({
                    value: option._id,
                    label: option.name,
                  }))}
                  value={selectedTags[phone._id] || []}
                  onChange={(selectedOptions) => handleChange(selectedOptions, {}, phone)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </Stack>
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default PhoneNumbers;
